import React, { useState } from 'react';
import { MessageCircle, ExternalLink, Copy, Check, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const TelegramConnect = ({ userId, onConnect, onError }) => {
  const [step, setStep] = useState('initial'); // initial, generating, ready, connecting
  const [linkData, setLinkData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const generateTelegramLink = async () => {
    try {
      setLoading(true);
      setStep('generating');

      const response = await fetch(`${BACKEND_URL}/api/telegram/generate-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setLinkData(data);
        setStep('ready');

        // Auto-open Telegram link on mobile devices
        if (window.innerWidth <= 768) {
          window.open(data.bot_link, '_blank');
        }
      } else {
        onError?.(data.error || 'Failed to generate Telegram link');
        setStep('initial');
      }
    } catch (error) {
      console.error('Error generating Telegram link:', error);
      onError?.('Network error. Please try again.');
      setStep('initial');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(linkData.bot_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = linkData.bot_link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openTelegramLink = () => {
    window.open(linkData.bot_link, '_blank');
    setStep('connecting');

    // Start polling for connection status
    pollConnectionStatus();
  };

  const pollConnectionStatus = () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/telegram/status/${userId}`);
        const data = await response.json();

        if (response.ok && data.connected) {
          clearInterval(pollInterval);
          setStep('initial');
          setLinkData(null);
          onConnect?.();
        }
      } catch (error) {
        console.error('Error polling connection status:', error);
      }
    }, 3000);

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (step === 'connecting') {
        setStep('ready');
      }
    }, 300000);
  };

  const renderStep = () => {
    switch (step) {
      case 'initial':
        return (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
              <MessageCircle className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Connecter Telegram</h4>
              <p className="text-sm text-gray-600 mb-4">
                Cliquez ci-dessous pour générer un lien de connexion sécurisé
              </p>
            </div>
            <button
              onClick={generateTelegramLink}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Génération...' : 'Connecter Telegram'}
            </button>
          </div>
        );

      case 'generating':
        return (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Génération du lien...</h4>
              <p className="text-sm text-gray-600">
                Création d'un lien de connexion sécurisé
              </p>
            </div>
          </div>
        );

      case 'ready':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="h-8 w-8 text-green-600" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Lien généré !</h4>
              <p className="text-sm text-gray-600 mb-4">
                Cliquez sur le bouton ci-dessous pour ouvrir Telegram et compléter la connexion
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={openTelegramLink}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                <MessageCircle className="h-5 w-5" />
                <span>Ouvrir Telegram</span>
                <ExternalLink className="h-4 w-4" />
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">ou</span>
                </div>
              </div>

              <div className="flex space-x-2">
                <input
                  type="text"
                  value={linkData?.bot_link || ''}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  title="Copier le lien"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>

              {copied && (
                <p className="text-sm text-green-600 text-center">Lien copié !</p>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5 mr-2" />
                <div className="text-sm">
                  <p className="text-blue-800 font-medium mb-1">Instructions :</p>
                  <ol className="text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Ouvrez Telegram en cliquant sur le bouton ci-dessus</li>
                    <li>Appuyez sur "Démarrer" ou envoyez /start</li>
                    <li>Revenez ici, la connexion sera automatique</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        );

      case 'connecting':
        return (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
              <div className="animate-pulse">
                <MessageCircle className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Connexion en cours...</h4>
              <p className="text-sm text-gray-600 mb-4">
                En attente de la confirmation depuis Telegram
              </p>
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-bounce h-2 w-2 bg-blue-600 rounded-full"></div>
                <div className="animate-bounce h-2 w-2 bg-blue-600 rounded-full" style={{ animationDelay: '0.1s' }}></div>
                <div className="animate-bounce h-2 w-2 bg-blue-600 rounded-full" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Cette page se mettra à jour automatiquement une fois la connexion établie
            </p>
            <button
              onClick={() => setStep('ready')}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Retour aux instructions
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      {renderStep()}
    </div>
  );
};

export default TelegramConnect;